/**
 * An app's config key, read with the defaults the app declares (I-2510).
 *
 * Master keeps no defaults of an app key (`mail.`, `calendar.`, `erp.`, …):
 * their platform layer is `{}`. What a field holds while no layer sets it is
 * the default in the app's settings declaration
 * (`ConnectAppModule.settingsSections`), and the settings list shows exactly
 * that. So every reader of an app key goes through this one helper: the
 * effective document is the declared defaults with master's answer merged
 * over them (`connectSettingsDefaults` ⊕ effective, objects member by member,
 * master wins) — the app and the list read the same value. A key without
 * declared fields reads as master answers it.
 */
import {
  useAccountConfig,
  type AccountConfigHandle,
  type UseAccountConfigOptions,
} from "./accountConfig";
import { isJsonObject, mergeJson, type JsonObject } from "./mergePatch";
import {
  connectSettingsDefaults,
  type ConnectSettingsSection,
} from "./settingsDeclaration";

/**
 * The document a key holds while no layer sets anything: the declared
 * defaults of the key's fields, with the reader's own `fallback` (fields it
 * does not declare) merged over them. Undefined when there is neither.
 */
export function declaredAccountConfigFallback(
  sections: readonly ConnectSettingsSection[],
  key: string,
  fallback?: unknown,
): JsonObject | undefined {
  const declared = connectSettingsDefaults(sections, key);
  const own = isJsonObject(fallback) ? fallback : undefined;
  if (!Object.keys(declared).length) return own;
  return own ? (mergeJson(declared, own) as JsonObject) : declared;
}

/**
 * `useAccountConfig` for an app's key: the shared store of the key, its
 * effective document laid over the defaults the app declares in `sections`.
 */
export function useDeclaredAccountConfig<T = JsonObject>(
  sections: readonly ConnectSettingsSection[],
  key: string,
  options: UseAccountConfigOptions<T> = {},
): AccountConfigHandle<T> {
  const fallback = declaredAccountConfigFallback(
    sections,
    key,
    options.fallback,
  );
  return useAccountConfig<T>(key, {
    ...options,
    fallback: fallback as T | undefined,
  });
}
